import { useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { Button } from '../../components/Button';
import { Field, Input, Select, Textarea } from '../../components/Field';
import { Card, Page } from '../../components/Page';
import { useDb } from '../../db/hooks';
import { useCanWrite } from '../../account/useCanWrite';
import { QUALITATIVE_OPTIONS } from '../../data/labCatalog';
import { resultFor, saveReport, useLabCatalog, useLabReports, useLabResults, type LabReport, type LabResult, type LabTest } from '../../data/labs';
import { describeRange, parseNumericLabValue } from '../../lib/labs';
import { localToday } from '../sessions/fields';
import { groupByPanel, LabStatusBadge, panelName } from './shared';

// Panels most monthly dialysis bloods include; opened by default on the first report.
const DEFAULT_OPEN = ['CBC', 'RFT', 'ELECTROLYTES', 'MIN_IRON'];

/** Add a report (/labs/new) or edit one (/labs/:id/edit). */
export function LabReportFormPage() {
  const { id } = useParams();
  const canWrite = useCanWrite();
  const { tests, isLoading: testsLoading } = useLabCatalog();
  const { reports, isLoading } = useLabReports();
  const { results, isLoading: resultsLoading } = useLabResults();
  if (isLoading || resultsLoading || testsLoading) return null;
  const report = id ? reports.find((item) => item.id === id) : undefined;
  if ((id && !report) || !canWrite) return <Navigate to={id ? `/labs/${id}` : '/labs'} replace />;
  return <LabReportForm report={report} tests={tests} reports={reports} results={results} />;
}

function LabReportForm({ report, tests, reports, results }: { report?: LabReport; tests: LabTest[]; reports: LabReport[]; results: LabResult[] }) {
  const db = useDb();
  const navigate = useNavigate();
  const existing = useMemo(() => (report ? results.filter((result) => result.report_id === report.id) : []), [report, results]);
  const previousReport = reports.find((item) => item.id !== report?.id);
  const [date, setDate] = useState(report?.result_date ?? localToday());
  const [labName, setLabName] = useState(report?.lab_name ?? previousReport?.lab_name ?? '');
  const [notes, setNotes] = useState(report?.notes ?? '');
  const [values, setValues] = useState(() => new Map(existing.map((result) => [result.test_id ?? '', result.raw_value ?? ''])));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inputs = useRef(new Map<string, HTMLInputElement | HTMLSelectElement>());

  // Tests with a value in this report show even if hidden from the catalog since.
  const visible = tests.filter((test) => !test.hidden || values.get(test.id));
  const groups = groupByPanel(visible, { includeHidden: true });
  const usedPanels = new Set(
    results.filter((result) => result.report_id === (report?.id ?? previousReport?.id)).map((result) => tests.find((test) => test.id === result.test_id)?.panel)
  );
  const [open, setOpen] = useState(() => new Set(usedPanels.size ? [...usedPanels].filter(Boolean) as string[] : DEFAULT_OPEN));
  const order = groups.flatMap((group) => (open.has(group.panel) ? group.tests.map((test) => test.id) : []));

  const invalid = visible.filter((test) => test.result_type === 'numeric' && (values.get(test.id) ?? '').trim() && parseNumericLabValue(values.get(test.id)!) == null);
  const filled = visible.filter((test) => (values.get(test.id) ?? '').trim()).length;

  function setValue(testId: string, value: string) {
    setValues((current) => new Map(current).set(testId, value));
  }

  function focusNext(testId: string) {
    const next = order[order.indexOf(testId) + 1];
    if (next) inputs.current.get(next)?.focus();
  }

  async function save() {
    setError(null);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || date > localToday()) return setError('Choose the date of the report (not in the future).');
    if (invalid.length) return setError(`Check ${invalid.map((test) => test.name).join(', ')}: enter a number.`);
    if (!filled) return setError('Enter at least one result.');
    setSaving(true);
    try {
      const saved = await saveReport(db, report?.id, { result_date: date, lab_name: labName.trim() || null, notes: notes.trim() || null }, values, tests, existing);
      navigate(`/labs/${saved.id}`, { replace: true });
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : String(saveError));
      setSaving(false);
    }
  }

  return (
    <Page title={report ? 'Edit report' : 'Add a report'} back={report ? `/labs/${report.id}` : '/labs'}>
      <Card>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Report date">
            <Input type="date" value={date} max={localToday()} onChange={(event) => setDate(event.target.value)} />
          </Field>
          <Field label="Lab (optional)">
            <Input value={labName} onChange={(event) => setLabName(event.target.value)} />
          </Field>
        </div>
      </Card>

      {groups.map((group) => {
        const isOpen = open.has(group.panel);
        const count = group.tests.filter((test) => (values.get(test.id) ?? '').trim()).length;
        return (
          <section key={group.panel} className="rounded-xl border border-clinical-line bg-clinical-card shadow-soft">
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={() => setOpen((current) => {
                const next = new Set(current);
                if (next.has(group.panel)) next.delete(group.panel);
                else next.add(group.panel);
                return next;
              })}
              className="focus-ring flex min-h-12 w-full items-center gap-2 rounded-xl px-4 text-left"
            >
              <span className="flex-1 text-sm font-semibold">{panelName(group.panel)}</span>
              {count ? <span className="text-xs text-clinical-muted">{count} entered</span> : null}
              <ChevronDown className={`h-4 w-4 text-clinical-muted transition ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen ? (
              <ul className="divide-y divide-clinical-line border-t border-clinical-line">
                {group.tests.map((test) => {
                  const raw = values.get(test.id) ?? '';
                  const status = raw.trim() ? resultFor(test, raw).status : null;
                  const range = describeRange(test);
                  return (
                    <li key={test.id} className="flex items-center gap-3 px-4 py-2">
                      <label htmlFor={`lab-${test.id}`} className="min-w-0 flex-1">
                        <span className="block text-sm font-medium">{test.name}</span>
                        <span className="block text-xs text-clinical-muted">{[test.unit, range ? `range ${range}` : null].filter(Boolean).join(' · ')}</span>
                      </label>
                      <div className="flex w-36 shrink-0 flex-col items-end gap-1">
                        {test.result_type === 'qualitative' ? (
                          <Select
                            id={`lab-${test.id}`}
                            aria-label={test.name}
                            ref={(element) => {
                              if (element) inputs.current.set(test.id, element);
                            }}
                            value={raw}
                            onChange={(event) => setValue(test.id, event.target.value)}
                          >
                            <option value="">–</option>
                            {[...new Set([...QUALITATIVE_OPTIONS, ...(raw ? [raw] : [])])].map((option) => (
                              <option key={option}>{option}</option>
                            ))}
                          </Select>
                        ) : (
                          <Input
                            id={`lab-${test.id}`}
                            aria-label={test.name}
                            ref={(element) => {
                              if (element) inputs.current.set(test.id, element);
                            }}
                            inputMode={test.result_type === 'numeric' ? 'decimal' : 'text'}
                            enterKeyHint="next"
                            autoComplete="off"
                            value={raw}
                            aria-invalid={invalid.includes(test)}
                            onChange={(event) => setValue(test.id, event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                focusNext(test.id);
                              }
                            }}
                            className="text-right tabular-nums"
                          />
                        )}
                        <LabStatusBadge status={status} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </section>
        );
      })}

      <Card>
        <Field label="Notes (optional)">
          <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Anything the doctor said about these results" />
        </Field>
      </Card>

      <div className="sticky bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-10 space-y-2 rounded-xl bg-clinical-surface/95 py-2 backdrop-blur md:bottom-0">
        {error ? <p className="text-sm text-clinical-danger">{error}</p> : null}
        <Button className="w-full" disabled={saving} onClick={() => void save()}>
          {saving ? 'Saving…' : `Save report${filled ? ` (${filled} ${filled === 1 ? 'result' : 'results'})` : ''}`}
        </Button>
      </div>
    </Page>
  );
}
